import { Download, FileSpreadsheet } from "lucide-react";
import { ContainerCard, EmployeeDetails, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { PAYROLL_PAY_SLIP } from "../../constants/api";
import { checkUserPermissionFunc, openPayslipPdf, formatPayrollCurrency } from "../../components/common/CommonFunctions";
import { useState } from "react";
import { Button } from "../../components/ui/button";
import { useOrgAdminBranchColumn } from "../../hooks/useOrgAdminBranchColumn";

const PaySlips = () => {

    const [payrollData, setPayrollData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const { edit: editPermission } = checkUserPermissionFunc("payroll", "payslips", "SubMenu") || {};
    const { insertBranchColumn } = useOrgAdminBranchColumn("employee_name");

    const columns = insertBranchColumn([
        {
            accessor: "employee_name",
            Header: "Employee",
            sortable: true,
            fixed: true,
            filterable: true,
            maxWidth: "350px",
            Cell: ({ value, row }) => {
                const record = row;

                let empDetails = {
                    ...record?.employee,
                    designation_id: record?.employee?.designation
                        ? {
                            id: record?.employee.designation.id,
                            designation_name: record?.employee.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: record?.employee?.employee_name || "-",
                    id: record?.employee?.employee_id || "-",
                };

                return <EmployeeDetails details={empDetails} size="xs" />;
            },
        },
        {
            accessor: "ctc",
            Header: "CTC",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-medium text-gray-900 dark:text-white">
                    ₹{value?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                </span>
            ),
        },
        {
            accessor: "earnings",
            Header: "Earnings",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-medium text-emerald-600 dark:text-emerald-400">
                    ₹{value?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                </span>
            ),
        },
        {
            accessor: "deductions",
            Header: "Deductions",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-medium text-rose-600 dark:text-rose-400">
                    ₹{value?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                </span>
            ),
        },
        {
            accessor: "lop_amount",
            Header: "LOP Amount",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-medium text-amber-600 dark:text-amber-400">
                    ₹{formatPayrollCurrency(value)}
                </span>
            ),
        },
        {
            accessor: "net_pay",
            Header: "Net Pay",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-bold text-emerald-600 dark:text-emerald-400">
                    ₹{value?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                </span>
            ),
        },
        {
            accessor: "ytd_earnings",
            Header: "YTD Earnings",
            sortable: false,
            width: "140px",
            Cell: ({ row }) => (
                <span className="font-medium text-emerald-700 dark:text-emerald-300">
                    ₹{Number(row?.ytd?.earnings || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
            ),
        },
        {
            accessor: "ytd_deductions",
            Header: "YTD Deductions",
            sortable: false,
            width: "140px",
            Cell: ({ row }) => (
                <span className="font-medium text-rose-700 dark:text-rose-300">
                    ₹{Number(row?.ytd?.deductions || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
            ),
        },
        {
            accessor: "ytd_net_pay",
            Header: "YTD Net Pay",
            sortable: false,
            width: "140px",
            Cell: ({ row }) => (
                <span className="font-bold text-teal-700 dark:text-teal-300" title={row?.ytd?.financial_year ? `FY ${row.ytd.financial_year} · up to ${row.ytd.upto_month}` : ''}>
                    ₹{Number(row?.ytd?.net_pay || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
            ),
        },
        {
            accessor: "reimbursements_total",
            Header: "Reimbursements",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-medium text-indigo-600 dark:text-indigo-400">
                    ₹{value?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                </span>
            ),
        },
        {
            accessor: "credits",
            Header: "Credits",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-medium text-emerald-600 dark:text-emerald-400">
                    ₹{value?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                </span>
            ),
        },
        {
            accessor: "debit",
            Header: "Debit",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-medium text-rose-600 dark:text-rose-400">
                    ₹{value?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                </span>
            ),
        },
        {
            accessor: "is_publish",
            Header: "Status",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value
                    ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                    : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                    }`}>
                    {value ? "Published" : "Draft"}
                </span>
            ),
        },
        ...(editPermission ? [
            {
                accessor: "actions",
                Header: "Actions",
                width: "90px",
                fixed: true,
                align: 'right',
                Cell: ({ row }) => {

                    const handleDownload = () => {
                        if (!row?.id) return;
                        openPayslipPdf(row.id);
                    };

                    return (
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleDownload(row)}>
                                <Download className="h-4 w-4" />
                            </Button>
                        </div>
                    )
                }
            }
        ] : [])
    ]);

    return (
        <ContainerCard scrollable>
            {/* Header */}
            <ModuleTitleCard Icon={FileSpreadsheet} title={'Processed Payslips'} subtitle="Manage and process employers Payslips" totalRecords={totalRecords} />

            <Reactable
                pagename="payslips"
                columns={columns}
                data={payrollData}
                setData={setPayrollData}
                setTotalCount={setTotalRecords} // Change this line
                serverSide={true}
                url={PAYROLL_PAY_SLIP}
                rowSize={editPermission ? "sm" : undefined}
                isCSVRequired
                isPDFRequired
                isMonthYearRequired
                isFromToDateRequired={false}
            />

        </ContainerCard>
    );
}

export default PaySlips;